import { cn, NoddiIcon } from 'noddi-ui-common';
import { NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import { MouseEvent, useId, useState } from 'react';
import { Button } from '../../../base/button/button';
import { Calendar } from '../../../base/calendar';
import { ScrollArea, ScrollBar } from '../../../base/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '../NoddiPopover';
import { NoddiTooltip } from '../NoddiTooltip';
import { ClearButton } from './ClearButton';
import { NoddiDatePickerProps } from './NoddiDatePicker';

const hours = Array.from({ length: 24 }, (_, i) => i).reverse();

const minutes = Array.from({ length: 12 }, (_, i) => i * 5);

export function NoddiDateTimePicker({
  date,
  onDateChange,
  id,
  name,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  showClearButton,
  popoverContentClassName,
  className,
  label,
  tooltip,
  ...rest
}: Omit<NoddiDatePickerProps, 'customTrigger'>) {
  const [isOpen, setIsOpen] = useState(false);
  const generatedId = useId();
  const controlId = id ?? name ?? generatedId;
  const labelIdentifier = `${controlId}-label`;
  const valueIdentifier = `${controlId}-value`;

  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      // If a date is selected, merge it with the current time from `date`
      const newDate = date
        ? new Date(selectedDate.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds()))
        : selectedDate;
      onDateChange(newDate);
    } else {
      onDateChange(undefined);
    }
  };

  const handleTimeChange = (type: 'hour' | 'minute', val: string) => {
    if (date) {
      const newDate = new Date(date);
      if (type === 'hour') {
        newDate.setHours(parseInt(val));
      } else if (type === 'minute') {
        newDate.setMinutes(parseInt(val));
      }
      onDateChange(newDate);
    } else {
      // If no date is selected yet, default to today's date with the selected time
      const now = new Date();
      if (type === 'hour') {
        now.setHours(parseInt(val));
        now.setMinutes(0); // Default minutes to 0 if only hour is selected
      } else if (type === 'minute') {
        now.setMinutes(parseInt(val));
        now.setHours(now.getHours()); // Keep current hour if only minute is selected
      }
      onDateChange(now);
    }
  };
  const handleClearDate = (e: MouseEvent) => {
    e.stopPropagation();
    onDateChange(undefined);
  };

  return (
    <div className={cn('flex flex-col gap-0.5')}>
      {label && (
        <div className='flex items-center gap-2'>
          <label id={labelIdentifier} htmlFor={controlId} className='text-sm'>
            {label}
          </label>
          {tooltip && <NoddiTooltip content={tooltip} label={`More information about ${label}`} />}
        </div>
      )}
      {/* Anchor box for the clear button: mirrors the trigger's sizing classes so the
          absolutely positioned clear button tracks the trigger, not the stretched column. */}
      <div className={cn('relative', className)}>
        <Popover open={isOpen} onOpenChange={setIsOpen} modal>
          <PopoverTrigger asChild>
            <Button
              type='button'
              variant='outline'
              id={controlId}
              name={name}
              aria-labelledby={label ? `${labelIdentifier} ${valueIdentifier}` : valueIdentifier}
              aria-describedby={ariaDescribedBy}
              aria-invalid={ariaInvalid}
              aria-haspopup='dialog'
              aria-expanded={isOpen}
              className={cn(
                'relative h-9 w-full min-w-70 justify-start text-left text-base font-normal md:text-sm',
                !date && 'text-muted-foreground',
                date && showClearButton && 'pr-12',
                className
              )}
            >
              <NoddiIcon name='Calendar' />
              <span id={valueIdentifier}>
                {date ? noddiFormatDate(date, NoddiDateFormats.DATE_TIME) : 'Pick a date and time'}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className={cn('w-auto overflow-hidden p-0', popoverContentClassName)}>
            <div className='sm:flex'>
              <Calendar
                // if number of months is 1 ( default date), its nice to hardcode the width, so it doesn't change when hiding days.
                className='w-62'
                mode='single'
                selected={date}
                onSelect={handleDateSelect}
                weekStartsOn={1}
                {...rest}
              />
              <div className='flex flex-col divide-y sm:h-75 sm:flex-row sm:divide-x sm:divide-y-0'>
                <ScrollArea className='w-64 sm:w-auto'>
                  <div role='group' aria-label='Hours' className='flex p-2 sm:flex-col'>
                    {hours.map((hour) => (
                      <Button
                        key={hour}
                        type='button'
                        aria-label={`Set hour ${hour.toString().padStart(2, '0')}`}
                        aria-pressed={Boolean(date && date.getHours() === hour)}
                        size='icon'
                        variant={date && date.getHours() === hour ? 'default' : 'ghost'}
                        className='aspect-square shrink-0 font-normal sm:w-full'
                        onClick={() => handleTimeChange('hour', hour.toString())}
                      >
                        {hour.toString().padStart(2, '0')}
                      </Button>
                    ))}
                  </div>
                  <ScrollBar orientation='horizontal' className='sm:hidden' />
                </ScrollArea>
                <ScrollArea className='w-64 sm:w-auto'>
                  <div role='group' aria-label='Minutes' className='flex p-2 sm:flex-col'>
                    {minutes.map((minute) => (
                      <Button
                        key={minute}
                        type='button'
                        aria-label={`Set minutes ${minute.toString().padStart(2, '0')}`}
                        aria-pressed={Boolean(date && date.getMinutes() === minute)}
                        size='icon'
                        variant={date && date.getMinutes() === minute ? 'default' : 'ghost'}
                        className='aspect-square shrink-0 font-normal sm:w-full'
                        onClick={() => handleTimeChange('minute', minute.toString())}
                      >
                        {minute.toString().padStart(2, '0')}
                      </Button>
                    ))}
                  </div>
                  <ScrollBar orientation='horizontal' className='sm:hidden' />
                </ScrollArea>
              </div>
            </div>
            <div className='flex justify-end border-t p-2'>
              <Button type='button' variant='ghost' onClick={() => setIsOpen(false)}>
                Done
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        {date && showClearButton && (
          <ClearButton handleClearDate={handleClearDate} label={label ? `Clear ${label}` : undefined} />
        )}
      </div>
    </div>
  );
}
